import { useMutation } from '@apollo/client';
import type { UpdateIngestionConfigMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateIngestionConfigDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import {
  type UpdateIngestionConfigInput,
  useUpdateIngestionConfigTRPC,
} from './useUpdateIngestionConfigTRPC';

/**
 * Flag-gated wrapper for the `updateIngestionConfig` mutation. Defaults to the
 * legacy Apollo mutation and delegates to the tRPC variant when the `trpc`
 * feature flag is enabled. Consumers import THIS, never the `*TRPC` twin.
 */
export const useUpdateIngestionConfig = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateGraphQL, graphqlState] = useMutation(
    UpdateIngestionConfigDocument,
    {
      update: (cache) => {
        evictField(cache, 'ingestion_config');
      },
    }
  );

  const trpcMutation = useUpdateIngestionConfigTRPC();

  const updateIngestionConfig = async (
    input: UpdateIngestionConfigInput
  ): Promise<UpdateIngestionConfigMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateIngestionConfig(input);
    }

    const result = await updateGraphQL({
      variables: {
        object: {
          Id: input.Id,
          IngestionConfig: input.IngestionConfig ?? undefined,
          OriginalTimestamp: input.OriginalTimestamp,
          ApiKey: input.ApiKey ?? undefined,
        },
      },
    });
    if (!result.data) {
      throw new Error('Failed to update ingestion config');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateIngestionConfig,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateIngestionConfig,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
